import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  TOOL_PREFERENCE_KEYS,
  getBooleanToolPreference,
  getPlaybackSpeedPreference,
  saveBooleanToolPreference,
  savePlaybackSpeedPreference,
  type PlaybackSpeed,
} from "../utils/toolPreferenceStorage";

interface ChatAudioPreferencesContextValue {
  isAudioOn: boolean;
  playbackSpeed: PlaybackSpeed;
  isReady: boolean;
  setIsAudioOn: (enabled: boolean) => void;
  setPlaybackSpeed: (speed: PlaybackSpeed) => void;
}

const ChatAudioPreferencesContext = createContext<ChatAudioPreferencesContextValue | null>(null);

export const ChatAudioPreferencesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAudioOn, setIsAudioOnState] = useState(true);
  const [playbackSpeed, setPlaybackSpeedState] = useState<PlaybackSpeed>(1);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const [audioPref, speedPref] = await Promise.all([
        getBooleanToolPreference(TOOL_PREFERENCE_KEYS.autoReadAloudEnabled, true),
        getPlaybackSpeedPreference(1),
      ]);
      if (cancelled) {
        return;
      }
      setIsAudioOnState(audioPref);
      setPlaybackSpeedState(speedPref);
      setIsReady(true);
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const setIsAudioOn = useCallback((enabled: boolean) => {
    setIsAudioOnState(enabled);
    void saveBooleanToolPreference(TOOL_PREFERENCE_KEYS.autoReadAloudEnabled, enabled);
  }, []);

  const setPlaybackSpeed = useCallback((speed: PlaybackSpeed) => {
    setPlaybackSpeedState(speed);
    void savePlaybackSpeedPreference(speed);
  }, []);

  const value = useMemo(
    () => ({
      isAudioOn,
      playbackSpeed,
      isReady,
      setIsAudioOn,
      setPlaybackSpeed,
    }),
    [isAudioOn, isReady, playbackSpeed, setIsAudioOn, setPlaybackSpeed]
  );

  return <ChatAudioPreferencesContext.Provider value={value}>{children}</ChatAudioPreferencesContext.Provider>;
};

export const useChatAudioPreferences = () => {
  const context = useContext(ChatAudioPreferencesContext);
  if (!context) {
    throw new Error("useChatAudioPreferences must be used within ChatAudioPreferencesProvider");
  }
  return context;
};
